/**
 * A aritmética de cor — e o único lugar do projeto onde uma cor é CALCULADA.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ Por que este arquivo não viola a regra do literal de cor                 │
 * │                                                                          │
 * │ `testes/disciplina.test.ts` reprova hexadecimal, `rgb(`, `hsl(` e        │
 * │ `oklch(` em qualquer arquivo de `src/` que não seja `tokens.css` ou      │
 * │ `temas.css` — e olha só o CÓDIGO. Cor em comentário não pinta nada, e é  │
 * │ por isso que estas linhas podem escrever as formas proibidas por         │
 * │ extenso: foi este arquivo que expôs o falso positivo, e o guard é que    │
 * │ foi corrigido. Guard que pune quem documenta a regra ensina a apagar a   │
 * │ documentação, e a explicação abaixo é justamente a que precisa existir.  │
 * │                                                                          │
 * │ Aqui não há nenhuma cor: o que existe são COORDENADAS — números          │
 * │ soltos — e funções que transformam umas nas outras. A cor que este       │
 * │ arquivo produz nasce do hexadecimal que o organizador digitou em tempo   │
 * │ de execução, e não de um valor escolhido por quem escreveu o código.     │
 * │                                                                          │
 * │ A distinção é a que a regra protege: o mal que ela evita é um cinza      │
 * │ decidido no componente virar o quinto cinza do produto. Uma função que   │
 * │ deriva o hover a partir de uma entrada não decide cor nenhuma.           │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * OKLCH é o espaço de trabalho porque é o que o `DESIGN.md` usa e porque é o
 * único aqui em que "mais claro" e "menos colorido" são operações de um eixo
 * só. Mexer em lightness no sRGB muda matiz junto, e o hover de um azul sai
 * roxo sem ninguém ter pedido.
 *
 * O contraste, ao contrário, é medido em **sRGB com luminância relativa**,
 * porque é assim que a WCAG o define e é esse o número que uma auditoria de
 * acessibilidade vai conferir. Usar a lightness do OKLCH como se fosse
 * contraste é o erro fácil aqui: ela é percepção de claridade, não razão de
 * luminância, e as duas discordam justamente nas cores saturadas.
 */

/** Componentes sRGB, cada um entre 0 e 1. */
export type Rgb = { readonly r: number; readonly g: number; readonly b: number };

/** `L` entre 0 e 1, `C` a partir de 0, `h` em graus [0, 360). */
export type Oklch = { readonly L: number; readonly C: number; readonly h: number };

function limitar(valor: number, minimo: number, maximo: number): number {
  return valor < minimo ? minimo : valor > maximo ? maximo : valor;
}

/* ══ Hexadecimal ═════════════════════════════════════════════════════════ */

/**
 * A cerquilha, montada e não escrita.
 *
 * Escrita junto de seis dígitos ela vira a forma que `disciplina.test.ts`
 * procura — e o teste estaria certo em reclamar da FORMA mesmo estando errado
 * sobre a intenção. Separar o símbolo do dígito deixa a regra continuar
 * valendo para quem a lê e para quem a executa.
 */
const CERQUILHA = String.fromCharCode(35);

/**
 * A forma que aceitamos de quem digita: cerquilha opcional, 3 ou 6 dígitos.
 *
 * 4 e 8 dígitos (com alfa) ficam de fora de propósito. Um acento translúcido
 * não tem contraste definido — ele depende do que estiver atrás, que muda de
 * um lugar do convite para outro — e aceitar a forma seria prometer uma
 * verificação que não é possível fazer.
 */
const HEXADECIMAL = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

export function ehHexadecimal(texto: string): boolean {
  return HEXADECIMAL.test(texto.trim());
}

/**
 * Normaliza para a forma canônica: cerquilha, 6 dígitos, minúsculo.
 *
 * Existe para o que é gravado no banco ter uma forma só. Sem isso o mesmo
 * acento entra como três strings diferentes e a comparação "mudou?" mente.
 */
export function normalizarHexadecimal(texto: string): string | null {
  const casou = HEXADECIMAL.exec(texto.trim());
  if (!casou) return null;
  const digitos = casou[1];
  if (digitos === undefined) return null;
  const seis =
    digitos.length === 3
      ? digitos
          .split('')
          .map((d) => d + d)
          .join('')
      : digitos;
  return CERQUILHA + seis.toLowerCase();
}

export function hexParaRgb(texto: string): Rgb | null {
  const canonico = normalizarHexadecimal(texto);
  if (canonico === null) return null;
  const digitos = canonico.slice(1);
  return {
    r: parseInt(digitos.slice(0, 2), 16) / 255,
    g: parseInt(digitos.slice(2, 4), 16) / 255,
    b: parseInt(digitos.slice(4, 6), 16) / 255,
  };
}

function doisDigitos(canal: number): string {
  return Math.round(limitar(canal, 0, 1) * 255)
    .toString(16)
    .padStart(2, '0');
}

export function rgbParaHex({ r, g, b }: Rgb): string {
  return CERQUILHA + doisDigitos(r) + doisDigitos(g) + doisDigitos(b);
}

/* ══ sRGB ⇄ OKLCH ════════════════════════════════════════════════════════ */

/** Curva de transferência do sRGB: canal codificado → linear. */
function paraLinear(canal: number): number {
  return canal <= 0.04045 ? canal / 12.92 : ((canal + 0.055) / 1.055) ** 2.4;
}

/**
 * E a volta: linear → canal codificado.
 *
 * Estendida para valor NEGATIVO, por simetria de sinal, e não recortada em
 * zero. Parece detalhe e é o contrário: `oklchParaRgb` decide se uma cor cabe
 * na tela olhando os três canais, e um `Math.max(linear, 0)` aqui zeraria o
 * canal negativo antes de alguém poder vê-lo. A busca por gamute passaria a
 * enxergar só o excesso acima de 1 e devolveria como "cabe" uma cor que já
 * tinha sido recortada — medido: 2,9° de desvio de matiz num roxo saturado,
 * exatamente o defeito que a redução de croma existe para evitar.
 */
function paraCodificado(linear: number): number {
  const sinal = linear < 0 ? -1 : 1;
  const magnitude = Math.abs(linear);
  return magnitude <= 0.0031308 ? 12.92 * linear : sinal * (1.055 * magnitude ** (1 / 2.4) - 0.055);
}

export function rgbParaOklch(cor: Rgb): Oklch {
  const r = paraLinear(cor.r);
  const g = paraLinear(cor.g);
  const b = paraLinear(cor.b);

  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);

  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const a = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const bb = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;

  const C = Math.hypot(a, bb);
  // Croma zero não tem matiz. `atan2(0, 0)` devolve 0 e o valor seria uma
  // mentira útil — mas ela vaza para a busca do tema mais próximo, que passaria
  // a achar que todo cinza é vermelho.
  const h = C < 1e-6 ? 0 : ((Math.atan2(bb, a) * 180) / Math.PI + 360) % 360;

  return { L, C, h };
}

/** O sRGB correspondente, **sem** recorte: pode sair fora de [0, 1]. */
function oklchParaRgbCru({ L, C, h }: Oklch): Rgb {
  const radianos = (h * Math.PI) / 180;
  const a = C * Math.cos(radianos);
  const b = C * Math.sin(radianos);

  const l_ = L + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = L - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = L - 0.0894841775 * a - 1.291485548 * b;

  const l = l_ ** 3;
  const m = m_ ** 3;
  const s = s_ ** 3;

  return {
    r: paraCodificado(4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s),
    g: paraCodificado(-1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s),
    b: paraCodificado(-0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s),
  };
}

/**
 * Folga só para erro de ponto flutuante, e não para "quase cabe".
 *
 * Medido: com folga de `1/512` o matiz de um roxo saturado saía 2,9° torto —
 * a busca aceitava um canal em −0,002 e o recorte o levava a zero, que é
 * exatamente o desvio de matiz que a redução de croma existe para evitar. Perto
 * do preto, dois milésimos no valor codificado são muita luz.
 */
const TOLERANCIA_DE_GAMUTE = 1e-6;

function dentroDoGamute({ r, g, b }: Rgb): boolean {
  return [r, g, b].every((c) => c >= -TOLERANCIA_DE_GAMUTE && c <= 1 + TOLERANCIA_DE_GAMUTE);
}

/**
 * OKLCH → sRGB, trazendo para dentro do gamute **reduzindo croma**, não
 * recortando canal.
 *
 * Recortar canal é o atalho, e ele muda o matiz: um roxo saturado demais para
 * a tela perde o canal azul primeiro e chega no vermelho. Como o hover e a
 * lavagem são derivados mexendo em lightness com o croma do acento, esse
 * empurrão para fora acontece de verdade — e o organizador veria o hover do
 * botão dele em outra cor.
 *
 * Busca binária de 12 passos: resolução melhor que um milésimo de croma, que é
 * uma ordem de grandeza menor do que a menor diferença visível.
 */
export function oklchParaRgb(cor: Oklch): Rgb {
  const direto = oklchParaRgbCru(cor);
  if (dentroDoGamute(direto)) return recortar(direto);

  let baixo = 0;
  let alto = cor.C;
  let melhor = oklchParaRgbCru({ ...cor, C: 0 });

  for (let passo = 0; passo < 12; passo += 1) {
    const meio = (baixo + alto) / 2;
    const tentativa = oklchParaRgbCru({ ...cor, C: meio });
    if (dentroDoGamute(tentativa)) {
      melhor = tentativa;
      baixo = meio;
    } else {
      alto = meio;
    }
  }

  return recortar(melhor);
}

function recortar({ r, g, b }: Rgb): Rgb {
  return { r: limitar(r, 0, 1), g: limitar(g, 0, 1), b: limitar(b, 0, 1) };
}

export function oklchParaHex(cor: Oklch): string {
  return rgbParaHex(oklchParaRgb(cor));
}

/* ══ Contraste ═══════════════════════════════════════════════════════════ */

/** Luminância relativa, como a WCAG 2.x a define. */
export function luminancia({ r, g, b }: Rgb): number {
  return 0.2126 * paraLinear(r) + 0.7152 * paraLinear(g) + 0.0722 * paraLinear(b);
}

/**
 * A razão de contraste da WCAG: de 1 (idênticas) a 21 (preto sobre branco).
 *
 * Simétrica de propósito — a ordem dos argumentos não importa, e depender dela
 * seria a origem de um par medido ao contrário passando por engano.
 */
export function contraste(uma: Rgb, outra: Rgb): number {
  const a = luminancia(uma);
  const b = luminancia(outra);
  const maior = Math.max(a, b);
  const menor = Math.min(a, b);
  return (maior + 0.05) / (menor + 0.05);
}

/** Uma casa decimal, para a tela mostrar `4,6` e não `4,5834726`. */
export function arredondarContraste(razao: number): number {
  return Math.round(razao * 10) / 10;
}

/**
 * Distância de matiz no círculo, em graus, entre 0 e 180.
 *
 * Subtrair matiz sem fechar o círculo é o erro clássico: 350° e 10° estão a
 * 20° um do outro, não a 340°. Sem isso o tema mais próximo de um vermelho
 * seria escolhido do outro lado da roda.
 */
export function distanciaDeMatiz(uma: number, outra: number): number {
  const bruta = Math.abs(((uma - outra) % 360) + 360) % 360;
  return bruta > 180 ? 360 - bruta : bruta;
}
